'use client';

import type { VariantProps } from 'class-variance-authority';
import { Ellipsis } from 'lucide-react';
import type { MouseEventHandler, ReactNode } from 'react';
import { Button, type buttonVariants } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { DropdownMenu, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

type SecondaryTriggerAction = {
  type: 'button';
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  ariaLabel?: string;
  icon?: ReactNode;
  className?: string;
};

type SecondaryMenuAction = {
  type: 'menu';
  renderMenu: () => ReactNode;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
};

type SecondaryAction = SecondaryTriggerAction | SecondaryMenuAction;

interface SummarizerActionControlsProperties {
  className?: string;
  disabled?: boolean;
  groupClassName?: string;
  label: string;
  onPrimaryClick: MouseEventHandler<HTMLButtonElement>;
  primaryClassName?: string;
  primaryVariant?: VariantProps<typeof buttonVariants>['variant'];
  secondaryAction?: SecondaryAction | null;
  secondaryVariant?: VariantProps<typeof buttonVariants>['variant'];
}

export function SummarizerActionControls({
  label,
  onPrimaryClick,
  disabled = false,
  className,
  primaryVariant = 'default',
  primaryClassName = 'h-8 px-3 text-xs font-semibold shadow-sm',
  secondaryAction,
  secondaryVariant = 'outline',
  groupClassName,
}: SummarizerActionControlsProperties) {
  const basePrimary = (
    <Button
      type="button"
      size="sm"
      variant={primaryVariant}
      className={primaryClassName}
      onClick={onPrimaryClick}
      disabled={disabled}
    >
      {label}
    </Button>
  );

  if (!secondaryAction) {
    return (
      <div className={cn('flex shrink-0 items-center text-xs', className)} data-no-conversation>
        {basePrimary}
      </div>
    );
  }

  const renderSecondary = () => {
    if (secondaryAction.type === 'button') {
      return (
        <Button
          type="button"
          size="icon"
          variant={secondaryVariant}
          className={cn('h-8 w-8', secondaryAction.className)}
          aria-label={secondaryAction.ariaLabel ?? 'Open summarizer options'}
          onClick={secondaryAction.onClick}
          disabled={secondaryAction.disabled}
        >
          {secondaryAction.icon ?? <Ellipsis className="h-4 w-4" aria-hidden="true" />}
        </Button>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant={secondaryVariant}
            className={cn('h-8 w-8', secondaryAction.className)}
            aria-label={secondaryAction.ariaLabel ?? 'More summarizer actions'}
            disabled={secondaryAction.disabled}
          >
            <Ellipsis className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        {secondaryAction.renderMenu()}
      </DropdownMenu>
    );
  };

  return (
    <div className={cn('flex shrink-0 items-center text-xs', className)} data-no-conversation>
      <ButtonGroup className={cn('shadow-none', groupClassName)}>
        {basePrimary}
        {renderSecondary()}
      </ButtonGroup>
    </div>
  );
}

export type { SecondaryAction as SummarizerSecondaryAction };
